import { useEffect, useMemo, useState } from "preact/hooks";
import type { IndexRecord } from "../../shared/snapshot-format";
import type { Bookmark, RecentEntry } from "../character";
import { clearRecents, listBookmarks, listRecents, setBookmark, subscribePersonal } from "../character";
import { EMPTY_FILTER, EMPTY_SCOPE, RARITIES, browseEntries, fieldFacets, hasFacets, recordAdapter, scopeCatalogue, traitFacets, type CatalogueScope, type EntryFilter } from "../data/browse";
import type { Search } from "../data/search";
import { featCategoryLabel, typeLabel } from "./labels";
import type { LibrarySection } from "./router";
import { useSheetActions } from "./sheet-context";
import { useCatalogue, useManifest, useSearch } from "./use-data";

interface Props {
  section: LibrarySection;
  onSection: (s: LibrarySection) => void;
  /** The global Legacy toggle (Settings.showLegacy): a browsing filter, never a legality rule. */
  legacy: boolean;
  onLegacy: (on: boolean) => void;
}

const PAGE = 100;
const SCOPE_KEY = "library:scope";
const FILTER_KEY = "library:filter";

type StoredScope = Omit<CatalogueScope, "legacy">;

function readStored<T>(key: string, fallback: T): T {
  try {
    const raw = sessionStorage.getItem(key);
    return raw ? { ...fallback, ...(JSON.parse(raw) as Partial<T>) } : fallback;
  } catch {
    return fallback;
  }
}

function store(key: string, value: unknown): void {
  try {
    sessionStorage.setItem(key, JSON.stringify(value));
  } catch {
    /* private mode: the state lives in memory only */
  }
}

/** Remember a search typed elsewhere (the home screen) so the Library opens on it. */
export function rememberSearch(text: string): void {
  store(FILTER_KEY, { ...readStored<EntryFilter>(FILTER_KEY, EMPTY_FILTER), text });
  store(SCOPE_KEY, { ...EMPTY_SCOPE, legacy: undefined });
}

/**
 * The Library (issue #15): the Pack browser and search over the prebuilt index, the Bookmarks
 * and the Recents. Lives on its own page from the home screen and as a tab of every Character;
 * the browsing state is shared between the two through sessionStorage, the personal lists
 * through IndexedDB. Tapping an Entry opens it in the page's Entry viewer.
 */
export function Library({ section, onSection, legacy, onLegacy }: Props) {
  const personal = usePersonal();
  return (
    <div class="library">
      <div class="segmented library-sections" role="tablist" aria-label="Library">
        {(["browse", "bookmarks", "recents"] as const).map((s) => (
          <button type="button" key={s} role="tab" aria-selected={section === s} class={section === s ? "active" : ""} onClick={() => onSection(s)}>
            {s === "browse" ? "Browse" : s === "bookmarks" ? "Bookmarks" : "Recents"}
            {s === "bookmarks" && personal.bookmarks.length > 0 ? <span class="badge">{personal.bookmarks.length}</span> : null}
          </button>
        ))}
      </div>
      {section === "browse" && <Browse legacy={legacy} onLegacy={onLegacy} marked={personal.marked} />}
      {section === "bookmarks" && <Bookmarks bookmarks={personal.bookmarks} legacy={legacy} />}
      {section === "recents" && <Recents recents={personal.recents} marked={personal.marked} />}
    </div>
  );
}

interface Personal {
  bookmarks: Bookmark[];
  recents: RecentEntry[];
  marked: Set<string>;
}

/** The personal lists, re-read whenever any screen changes them. */
function usePersonal(): Personal {
  const [state, setState] = useState<Personal>({ bookmarks: [], recents: [], marked: new Set() });
  useEffect(() => {
    let live = true;
    const read = () =>
      Promise.all([listBookmarks(), listRecents()]).then(([bookmarks, recents]) => {
        if (live) setState({ bookmarks, recents, marked: new Set(bookmarks.map((b) => b.uuid)) });
      }, () => undefined);
    void read();
    const off = subscribePersonal(() => void read());
    return () => {
      live = false;
      off();
    };
  }, []);
  return state;
}

/* ------------------------------ Browse ------------------------------ */

function Browse({ legacy, onLegacy, marked }: { legacy: boolean; onLegacy: (on: boolean) => void; marked: Set<string> }) {
  const records = useCatalogue();
  const search = useSearch();
  const manifest = useManifest();
  const [stored, setStored] = useState<StoredScope>(() => readStored<StoredScope>(SCOPE_KEY, EMPTY_SCOPE));
  const [filter, setFilterState] = useState<EntryFilter>(() => readStored<EntryFilter>(FILTER_KEY, EMPTY_FILTER));
  const [shown, setShown] = useState(PAGE);
  const scope: CatalogueScope = { ...stored, legacy };
  const setScope = (next: StoredScope) => {
    setStored(next);
    store(SCOPE_KEY, next);
    setShown(PAGE);
  };
  const setFilter = (next: EntryFilter) => {
    setFilterState(next);
    store(FILTER_KEY, next);
    setShown(PAGE);
  };

  // The scope without the type-level narrowing, for the facets each select offers.
  const inPack = useMemo(() => (records ? scopeCatalogue(records, { ...scope, type: null, category: null, publication: null }) : []), [records, scope.pack, legacy]);
  const scoped = useMemo(() => (records ? scopeCatalogue(records, scope) : []), [records, scope.pack, scope.type, scope.category, scope.publication, legacy]);
  const results = useMemo(() => browseEntries(scoped, filter, search, recordAdapter), [scoped, filter, search]);
  const traits = useMemo(() => traitFacets(scoped, recordAdapter), [scoped]);
  const types = useMemo(() => fieldFacets(inPack, "t"), [inPack]);
  const categories = useMemo(() => (scope.type === "feat" ? fieldFacets(inPack.filter((r) => r.t === "feat"), "c") : []), [inPack, scope.type]);
  const publications = useMemo(() => fieldFacets(inPack, "pub"), [inPack]);

  const browsing = filter.text.trim() !== "" || stored.pack !== null || stored.type !== null || stored.publication !== null || hasFacets(filter);
  const packLabel = (id: string) => manifest?.packs.find((p) => p.id === id)?.label ?? id;
  const reset = () => {
    setScope({ ...EMPTY_SCOPE });
    setFilter({ ...EMPTY_FILTER });
  };

  return (
    <div class="browse">
      <div class="inline">
        <input type="search" aria-label="Search every Pack" placeholder={records ? (search ? "Search names, traits and descriptions" : "Search by name") : "Loading the catalogue…"} value={filter.text} disabled={!records} onInput={(e) => setFilter({ ...filter, text: (e.currentTarget as HTMLInputElement).value })} />
        <label class="toggle">
          <input type="checkbox" checked={legacy} onChange={(e) => onLegacy((e.currentTarget as HTMLInputElement).checked)} /> Show Legacy
        </label>
      </div>
      <div class="inline scope">
        <select aria-label="Pack" value={stored.pack ?? ""} onChange={(e) => setScope({ ...stored, pack: (e.currentTarget as HTMLSelectElement).value || null, type: null, category: null, publication: null })}>
          <option value="">Every Pack</option>
          {manifest?.packs.map((p) => <option key={p.id} value={p.id}>{p.label}</option>)}
        </select>
        <select aria-label="Type" value={stored.type ?? ""} onChange={(e) => setScope({ ...stored, type: (e.currentTarget as HTMLSelectElement).value || null, category: null })}>
          <option value="">Every type</option>
          {types.map((t) => <option key={t.value} value={t.value}>{typeLabel(t.value)} ({t.count})</option>)}
        </select>
        {categories.length > 0 && (
          <select aria-label="Feat category" value={stored.category ?? ""} onChange={(e) => setScope({ ...stored, category: (e.currentTarget as HTMLSelectElement).value || null })}>
            <option value="">Every category</option>
            {categories.map((c) => <option key={c.value} value={c.value}>{featCategoryLabel(c.value)} ({c.count})</option>)}
          </select>
        )}
        <select aria-label="Publication" value={stored.publication ?? ""} onChange={(e) => setScope({ ...stored, publication: (e.currentTarget as HTMLSelectElement).value || null })}>
          <option value="">Every Publication</option>
          {publications.map((p) => <option key={p.value} value={p.value}>{p.value} ({p.count})</option>)}
        </select>
      </div>
      <EntryFilterBar filter={filter} onChange={setFilter} traits={traits} />
      {!records ? (
        <p class="muted">Loading the catalogue…</p>
      ) : !browsing ? (
        <PackList packs={manifest?.packs.map((p) => ({ id: p.id, label: p.label, count: inPack.filter((r) => r.p === p.id).length })) ?? []} onPick={(id) => setScope({ ...stored, pack: id })} />
      ) : (
        <>
          <p class="muted results-count" role="status">
            {results.length === 0 ? `Nothing matches${legacy ? "" : " among Remaster Entries (show Legacy to widen)"}.` : `${results.length.toLocaleString()} ${results.length === 1 ? "Entry" : "Entries"}${stored.pack ? ` in ${packLabel(stored.pack)}` : ""}`}
            {" "}<button type="button" class="linkish" onClick={reset}>reset</button>
          </p>
          <EntryList records={results.slice(0, shown)} search={search} marked={marked} />
          {results.length > shown && <button type="button" class="more" onClick={() => setShown(shown + PAGE)}>Show {Math.min(PAGE, results.length - shown)} more</button>}
        </>
      )}
    </div>
  );
}

function PackList({ packs, onPick }: { packs: { id: string; label: string; count: number }[]; onPick: (id: string) => void }) {
  return (
    <ul class="packs">
      {packs.map((p) => (
        <li key={p.id}>
          <button type="button" class="pack card" onClick={() => onPick(p.id)}>
            <strong>{p.label}</strong>
            <span class="muted">{p.count.toLocaleString()} {p.count === 1 ? "Entry" : "Entries"}</span>
          </button>
        </li>
      ))}
    </ul>
  );
}

/* ------------------------------ the shared filter bar ------------------------------ */

interface FilterBarProps {
  filter: EntryFilter;
  onChange: (next: EntryFilter) => void;
  /** Traits present in what is being filtered, most frequent first. */
  traits: { trait: string; count: number }[];
  /** "Level" by default; "Rank" for spells. */
  levelLabel?: string;
  /** A search box here too (the pickers), with this placeholder. */
  searchPlaceholder?: string;
  children?: preact.ComponentChildren;
}

/** Level range, rarity, traits and sort: the browser's filters, applied by the pickers on top of their candidate lists (#15). */
export function EntryFilterBar({ filter, onChange, traits, levelLabel = "Level", searchPlaceholder, children }: FilterBarProps) {
  const [open, setOpen] = useState(hasFacets(filter));
  const number = (v: string): number | null => (v.trim() === "" ? null : Math.max(0, Math.min(30, Number(v) || 0)));
  const addTrait = (t: string) => {
    if (t && !filter.traits.includes(t)) onChange({ ...filter, traits: [...filter.traits, t] });
  };
  return (
    <div class="filter-bar">
      {(searchPlaceholder !== undefined || children) && (
        <div class="inline">
          {searchPlaceholder !== undefined && <input type="search" aria-label="Search" placeholder={searchPlaceholder} value={filter.text} onInput={(e) => onChange({ ...filter, text: (e.currentTarget as HTMLInputElement).value })} />}
          {children}
          <button type="button" aria-expanded={open} onClick={() => setOpen(!open)}>{open ? "Hide filters" : hasFacets(filter) ? "Filters ●" : "Filters"}</button>
        </div>
      )}
      {(open || searchPlaceholder === undefined) && (
        <div class="inline filters">
          <label class="range">
            <span class="muted">{levelLabel}</span>
            <input type="number" inputMode="numeric" min="0" max="30" aria-label={`${levelLabel} from`} placeholder="min" value={filter.levelMin ?? ""} onInput={(e) => onChange({ ...filter, levelMin: number((e.currentTarget as HTMLInputElement).value) })} />
            <span class="muted">–</span>
            <input type="number" inputMode="numeric" min="0" max="30" aria-label={`${levelLabel} to`} placeholder="max" value={filter.levelMax ?? ""} onInput={(e) => onChange({ ...filter, levelMax: number((e.currentTarget as HTMLInputElement).value) })} />
          </label>
          <select aria-label="Rarity" value={filter.rarities.length === 1 ? filter.rarities[0] : filter.rarities.length > 1 ? "uncommon+" : ""} onChange={(e) => {
            const v = (e.currentTarget as HTMLSelectElement).value;
            onChange({ ...filter, rarities: v === "" ? [] : v === "uncommon+" ? ["uncommon", "rare", "unique"] : [v] });
          }}>
            <option value="">Any rarity</option>
            {RARITIES.map((r) => <option key={r} value={r}>{r}</option>)}
            <option value="uncommon+">uncommon and rarer</option>
          </select>
          <select aria-label="Add a trait" value="" onChange={(e) => { addTrait((e.currentTarget as HTMLSelectElement).value); (e.currentTarget as HTMLSelectElement).value = ""; }}>
            <option value="">Add a trait…</option>
            {traits.filter((t) => !filter.traits.includes(t.trait)).map((t) => <option key={t.trait} value={t.trait}>{t.trait} ({t.count})</option>)}
          </select>
          <span class="segmented" role="group" aria-label="Sort">
            <button type="button" class={filter.sort === "name" ? "active" : ""} aria-pressed={filter.sort === "name"} onClick={() => onChange({ ...filter, sort: "name" })}>Name</button>
            <button type="button" class={filter.sort === "level" ? "active" : ""} aria-pressed={filter.sort === "level"} onClick={() => onChange({ ...filter, sort: "level" })}>{levelLabel}</button>
          </span>
          {filter.traits.map((t) => (
            <button type="button" key={t} class="chip" aria-label={`Remove trait ${t}`} onClick={() => onChange({ ...filter, traits: filter.traits.filter((x) => x !== t) })}>{t} ×</button>
          ))}
          {hasFacets(filter) && <button type="button" class="linkish" onClick={() => onChange({ ...filter, levelMin: null, levelMax: null, traits: [], rarities: [] })}>clear filters</button>}
        </div>
      )}
    </div>
  );
}

/* ------------------------------ rows and lists ------------------------------ */

function EntryList({ records, search, marked, extra }: { records: IndexRecord[]; search: Search | null; marked: Set<string>; extra?: (r: IndexRecord) => preact.ComponentChildren }) {
  if (records.length === 0) return null;
  return (
    <ul class="library-list">
      {records.map((r) => <EntryRow key={r.u} record={r} snippet={search?.snippet(r) ?? ""} marked={marked.has(r.u)} extra={extra?.(r)} />)}
    </ul>
  );
}

function EntryRow({ record: r, snippet, marked, extra }: { record: IndexRecord; snippet: string; marked: boolean; extra?: preact.ComponentChildren }) {
  const { openEntry } = useSheetActions();
  const kind = r.t === "feat" && r.c ? featCategoryLabel(r.c) : r.t === "spell" ? (r.tr.includes("cantrip") ? "Cantrip" : r.tr.includes("focus") ? "Focus spell" : "Spell") : typeLabel(r.t);
  return (
    <li>
      <button type="button" class="pick" onClick={() => openEntry(r.u)}>
        <span class="pick-name">
          {r.n}
          {r.l !== null && r.l > 0 ? <span class="muted"> {r.l}</span> : null}
        </span>
        <span class="pick-tags">
          <span class="tag">{kind}</span>
          {r.r !== "common" && <span class={`tag rarity-${r.r}`}>{r.r}</span>}
          {!r.rm && <span class="tag legacy">Legacy</span>}
        </span>
        {snippet && <span class="snippet muted">{snippet}</span>}
      </button>
      {extra ?? (
        <button type="button" class={`icon star${marked ? " on" : ""}`} aria-label={marked ? `Remove Bookmark ${r.n}` : `Bookmark ${r.n}`} aria-pressed={marked} onClick={() => void setBookmark(r.u, !marked).catch(() => undefined)}>{marked ? "★" : "☆"}</button>
      )}
    </li>
  );
}

function Bookmarks({ bookmarks, legacy }: { bookmarks: Bookmark[]; legacy: boolean }) {
  const records = useCatalogue();
  const search = useSearch();
  const byUuid = useMemo(() => new Map((records ?? []).map((r) => [r.u, r])), [records]);
  const rows = bookmarks.map((b) => byUuid.get(b.uuid)).filter((r): r is IndexRecord => r !== undefined);
  const missing = records ? bookmarks.length - rows.length : 0;
  return (
    <div class="bookmarks">
      {!records ? <p class="muted">Loading the catalogue…</p> : bookmarks.length === 0 ? (
        <div class="card empty">
          <p>No Bookmarks yet.</p>
          <p class="muted">Tap ☆ on an Entry to find it here again. Bookmarks stay on this device.</p>
        </div>
      ) : (
        <>
          {!legacy && rows.some((r) => !r.rm) && <p class="muted">Legacy Bookmarks are listed whatever the Legacy toggle says.</p>}
          <EntryList records={rows} search={search} marked={new Set(rows.map((r) => r.u))} extra={(r) => <button type="button" aria-label={`Remove Bookmark ${r.n}`} onClick={() => void setBookmark(r.u, false).catch(() => undefined)}>Remove</button>} />
          {missing > 0 && <p class="muted">{missing} Bookmark{missing === 1 ? "" : "s"} point{missing === 1 ? "s" : ""} at Entries this Snapshot no longer ships.</p>}
        </>
      )}
    </div>
  );
}

function Recents({ recents, marked }: { recents: RecentEntry[]; marked: Set<string> }) {
  const records = useCatalogue();
  const search = useSearch();
  const byUuid = useMemo(() => new Map((records ?? []).map((r) => [r.u, r])), [records]);
  const rows = recents.map((b) => byUuid.get(b.uuid)).filter((r): r is IndexRecord => r !== undefined);
  return (
    <div class="recents">
      {!records ? <p class="muted">Loading the catalogue…</p> : recents.length === 0 ? (
        <div class="card empty">
          <p>Nothing viewed yet.</p>
          <p class="muted">The last 50 Entries opened in the viewer are listed here, newest first.</p>
        </div>
      ) : (
        <>
          <p class="row">
            <span class="muted">{rows.length} recent {rows.length === 1 ? "Entry" : "Entries"}, newest first.</span>
            <button type="button" onClick={() => void clearRecents().catch(() => undefined)}>Clear Recents</button>
          </p>
          <EntryList records={rows} search={search} marked={marked} />
        </>
      )}
    </div>
  );
}
